import { useCallback } from 'react';

import { fireCustomCrossAppAnalytic } from '@atlassiansox/analytics-cross-product-interaction-client/fire-cross-product-analytic';

import { useAnalyticsEvents as useAnalyticsNextEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { TownsquareApp } from '../store/context';

/**
 * Returns a function that fires the "view profile clicked" cross-app analytic
 * routed to the Teams destination app. Steps:
 * 1. Build the cross-product payload using the active townsquare app.
 * 2. Forward `createAnalyticsEvent` from `@atlaskit/analytics-next` so the
 *    shared `fireCustomCrossAppAnalytic` helper can dispatch the event.
 */
export const useFireCrossAppProfileAnalytic = (
	townsquareApp: TownsquareApp | undefined,
): (() => void) => {
	// `fireCustomCrossAppAnalytic` needs raw `createAnalyticsEvent`, so this hook
	// intentionally owns the `@atlaskit/analytics-next` dependency.
	const { createAnalyticsEvent } = useAnalyticsNextEvents();

	return useCallback(() => {
		if (!townsquareApp) {
			return;
		}
		fireCustomCrossAppAnalytic({
			tags: ['townsquare', 'navigationExperiences'],
			analyticsEventFireName: 'atlas',
			destinationApp: 'teams',
			bridge: `${townsquareApp}-customLink`,
			source: `townsquare-${townsquareApp}`,
			actionSubjectId: 'viewProfileLink',
			createAnalyticsEvent,
		});
	}, [townsquareApp, createAnalyticsEvent]);
};
